import { useEffect, useRef, useState } from "react";
import _ from "underscore";

import { Box, Loader } from "@braintrust/braintrust-ui-components";
import type { PdfViewerProps } from "@js/apps/common/components/pdf-viewer";
import { PdfViewer } from "@js/apps/common/components/pdf-viewer";

type EmbeddedResumeViewerProps = {
  sourceFile: string;
  page?: number;
  onNumPagesLoaded?: (numPages: number) => void;
};

export const EmbeddedResumeViewer = ({
  sourceFile,
  page,
  onNumPagesLoaded,
}: EmbeddedResumeViewerProps) => {
  const contentBox = useRef<HTMLDivElement>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfHasPassword, setPdfHasPassword] = useState(false);
  const [scale, setScale] = useState(0.98);
  const [pageWidth, setPageWidth] = useState(595);

  useEffect(() => {
    const handleResize = () => {
      if (contentBox.current) {
        const contentBoxWidth =
          contentBox.current?.getBoundingClientRect().width || 0;

        const newScale = contentBoxWidth / pageWidth;
        setScale(newScale);
      }
    };
    const debouncedResize = _.debounce(handleResize, 300);
    handleResize();
    window.addEventListener("resize", debouncedResize);

    return () => removeEventListener("resize", debouncedResize);
  }, [pageWidth]);

  const onLoadSuccess: PdfViewerProps["onLoadSuccess"] = async (pdf) => {
    onNumPagesLoaded?.(pdf.numPages);
    try {
      const resumeFirstPage = await pdf.getPage(1);
      const width = resumeFirstPage.view[2];
      setPageWidth(width);
      setPdfLoading(false);
    } catch (e) {
      setPdfLoading(false);
      void e;
    }
  };

  const onPassword = () => {
    setPdfHasPassword(true);
  };

  return (
    <Box ref={contentBox} sx={{ textAlign: "center" }}>
      {pdfHasPassword && (
        <Box sx={{ p: 12 }}>
          Preview can't be shown because this document is password-protected.
        </Box>
      )}
      {!pdfHasPassword && (
        <>
          {pdfLoading && (
            <Box sx={{ p: 12, positon: "relative", minHeight: "300px" }}>
              <Loader centered />
            </Box>
          )}
          <PdfViewer
            sourceFile={sourceFile}
            onLoadSuccess={onLoadSuccess}
            onPassword={onPassword}
            page={page}
            adjustCustomScale={() => scale}
            pdfViewerContainerProps={{
              sx: {
                border: pdfLoading ? undefined : "1px solid var(--soft-beige)",
                borderRadius: "16px",
                overflow: "hidden",
              },
            }}
          />
        </>
      )}
    </Box>
  );
};
